/**
 * Shared tune-store bridge between the /insects tuner and the game.
 *
 * Backed by a tiny Node service (see server/tune-server.mjs) proxied through
 * nginx at /api/tune. The tuner POSTs the full config; the game fetches it
 * once at boot and caches.
 */

export interface AssetTune {
  scale: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  offY: number;
}

export type TuneStoreData = Record<string, AssetTune>;

const API_URL = '/api/tune';

let cache: TuneStoreData = {};
let loaded = false;

/** Fetch tune config from the server. Call once at boot before constructing renderers. */
export async function loadTuneStore(): Promise<void> {
  try {
    const res = await fetch(API_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`GET /api/tune → ${res.status}`);
    const json = await res.json();
    if (json && typeof json === 'object') cache = json as TuneStoreData;
  } catch (err) {
    console.warn('TuneStore load failed; using defaults.', err);
  }
  loaded = true;
}

/** Apply per-world overrides to the in-memory tune cache. Called by
 *  main.ts at boot with the current WorldConfig's worldEntries. Each
 *  override is a partial (scale/rot/offY may be missing); we merge on
 *  top of whatever the global /api/tune had so un-set fields still use
 *  the global default. Subsequent readTune calls see the merged values.
 *
 *  IMPORTANT: this mutates the in-memory cache only — it never POSTs
 *  back to /api/tune. The global file stays untouched so the override
 *  is purely per-world. */
export function applyTuneOverrides(overrides: Record<string, Partial<AssetTune>>): void {
  for (const id in overrides) {
    const o = overrides[id];
    if (!o || Object.keys(o).length === 0) continue;
    cache[id] = { ...(cache[id] ?? {}), ...o };
  }
}

/** Synchronous read after loadTuneStore() has resolved. Returns defaults if missing. */
export function readTune(id: string, defaults: AssetTune): AssetTune {
  if (!loaded) {
    // Calling before load — not fatal, just returns defaults. Emit a warning
    // so the mistake shows up.
    console.warn(`readTune('${id}') called before loadTuneStore() resolved`);
  }
  const entry = cache[id];
  if (!entry) return defaults;
  return { ...defaults, ...entry };
}

export function getAllTunes(): TuneStoreData {
  return cache;
}

/** Overwrite the server-side config with the given full object. */
export async function saveTuneStore(next: TuneStoreData): Promise<void> {
  cache = next;
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(next),
  });
  if (!res.ok) throw new Error(`POST /api/tune → ${res.status}`);
}

/** Merge a single asset tune and save. */
export async function saveAssetTune(id: string, t: AssetTune): Promise<void> {
  cache = { ...cache, [id]: t };
  await saveTuneStore(cache);
}
